import { fullLabel } from "../WhereItIs";
import { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, Search, Building2 } from "lucide-react";
import { SearchCameraButton } from "@/components/search/engine";

// A BUILT-IN RACK MOVES ONLY DELIBERATELY, WITH PERMISSION — never by a scan. This is the only
// door, and it states what comes with it BEFORE it commits, because a rack carries its shelves and
// every part standing on them.
export const AssignSectorDialog = ({ node, sectors, onClose, onDone }) => {
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ql = q.trim().toLowerCase();
  const list = useMemo(() => (sectors || [])
    .filter((s) => !ql || `${s.short_code} ${fullLabel(s) || s.name}`.toLowerCase().includes(ql))
    .slice(0, 40), [sectors, ql]);

  const run = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/locations/${node.id}/move`, { new_parent_id: pick.id });
      setResult(data);
      toast.success(`${node.short_code} is now in ${fullLabel(data) || pick.short_code}.`);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!node} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="assign-sector-dialog" className="rounded-xl border-slate-200 sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Put {node?.short_code} in a sector</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              <b>{node?.name}</b> is built in — it can never be moved by a scan. Choosing a sector here is the only
              way it moves, and <b>everything on it comes with it</b>: its shelves stay its shelves and every part
              standing on them stays exactly where it is. The printed sticker keeps working.
            </p>
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="assign-sector-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search sectors — code, name or path…" className="rounded-lg border-slate-200 pl-9 h-11" />
              </div>
              <SearchCameraButton testid="assign-sector-search" onScan={setQ} size="h-11" />
            </div>
            <div className="max-h-[42dvh] overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {list.length === 0 && <div className="p-6 text-center font-mono text-xs text-slate-400">NO SECTORS MATCH</div>}
              {list.map((s) => (
                <button key={s.id} data-testid={`assign-sector-opt-${s.short_code}`} onClick={() => setPick(s)}
                  className={`w-full min-h-[48px] flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${pick?.id === s.id ? "bg-amber-50" : "hover:bg-slate-50"}`}>
                  <Building2 className="w-4 h-4 text-[#B01E28] shrink-0" />
                  <span className="font-mono text-xs shrink-0">{s.short_code}</span>
                  <span className="flex-1 break-words text-sm text-slate-600">{fullLabel(s) || s.name}</span>
                  {pick?.id === s.id && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div data-testid="assign-sector-result" className="rounded-lg border-2 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-800">
            <div className="font-semibold"><span className="font-mono">{node?.short_code}</span> is now in <span className="font-mono">{fullLabel(result)}</span>.</div>
            <div className="text-emerald-700 mt-0.5">
              {result.places_under} {result.places_under === 1 ? "shelf" : "shelves"} and {result.parts_with_it} part{result.parts_with_it === 1 ? "" : "s"} came with it. Every sticker still scans.
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-lg border-slate-200 h-11" onClick={onClose}>{result ? "Done" : "Cancel"}</Button>
          {!result && (
            <Button data-testid="assign-sector-confirm" disabled={busy || !pick} onClick={run}
              className="rounded-lg h-11 bg-[#E0781A] hover:bg-[#C76714] text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Put it in ${pick?.short_code || "a sector"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
